import React from 'react';
import { useSmsEnrollment, type SmsEnrollmentError } from '../../hooks/useSmsEnrollment';
import { formatPhoneE164 } from '../../utils/phone';
import { Button } from '../buttons/Button';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { PasswordInput } from '../forms/PasswordInput';

/**
 * SMS second-factor enrollment — CU-86ajwpnek §4.
 *
 * ⚠️ ONE COMPONENT, TWO HOSTS. Mounted on the profile page (optional,
 * pre-cutover) and inside the MFA route gate (mandatory, post-cutover). The
 * mobile app does the same — `MfaEnrollmentGateScreen` renders
 * `SmsEnrollmentScreen` inline rather than navigating away. Building a second
 * copy for the gate would guarantee drift on a security surface.
 *
 * The hosts differ only in framing and in what `onCancel` means:
 *   • profile — collapses the section
 *   • gate    — returns to the FACTOR CHOICE, never out of the gate
 *
 * ⚠️ STRUCTURAL SAFETY PROPERTY: there is NO uid input anywhere on this
 * surface — no uid field, no search box, no lookup, no uid in props. The server
 * binds the authorization to `req.auth.uid`, so enrolling for someone else is
 * not expressible here. This is stronger than labelling; do not add a uid prop
 * "for convenience".
 */

const RECAPTCHA_CONTAINER_ID = 'sms-enroll-recaptcha';

/**
 * ⚠️ Patient-facing copy. The console's equivalent says "admin" throughout and
 * references the Firebase console, which patients do not have.
 */
function errorCopy(error: SmsEnrollmentError): string {
  switch (error.kind) {
    case 'already-enrolled':
      return "Your account already has a text-message code set up, so there's nothing to add. If you need to change your number, please contact info@mideohealth.com.";
    case 'bad-password':
      return "That password didn't match. Please try again.";
    case 'stale-session':
      return "You've been here a little while, so we need your password once more. Please enter it again.";
    case 'invalid-phone':
      // Deliberately distinct from region-not-supported: this is a typo, and
      // telling the patient their region is unsupported would steer them off
      // SMS entirely when all they did was mistype a digit.
      return "That doesn't look like a complete mobile number. Please check it and try again.";
    case 'region-not-supported':
      // ⚠️ Never name the supported countries here. With a patient population
      // this small, "the one country we have a patient in" edges toward
      // re-identifying information.
      return "We can't send text messages to that number's country yet. Please try a different mobile number, or contact info@mideohealth.com and we'll help you find another way in.";
    case 'unverified-email':
      return 'Your account email needs to be verified first. Check your inbox for a verification link, then come back and try again.';
    case 'too-many-requests':
      // Never "try again in a moment" — and, since CU-86ajtgauu, never "a few
      // minutes" either. The one block we have MEASURED on this path
      // (MIDEO-UNIFIED-88, mobile `enroll-send-otp`) lasted HOURS, and Google
      // publishes no duration. A wrong timeframe sends the patient to support
      // frustrated, so no timeframe is stated. Matches the mobile app.
      return 'Too many attempts. For security, please wait a while before trying again.';
    case 'resend-cap-reached':
      return "We've sent several codes already. Please start over, or contact info@mideohealth.com if the messages aren't arriving.";
    case 'send-failed':
      return "We couldn't send your code just now. Please try again in a moment.";
    case 'bad-code':
      return "That code didn't match. Please check it and try again.";
    case 'offline':
      // CU-86akbg4qm — same wording as the login challenge. Unscoped below:
      // every step makes a request, so any of them can raise it.
      return "We couldn't reach MIDEO. Please check your internet connection and try again.";
    case 'generic':
    default:
      return 'Something went wrong. Please try again in a moment, or contact info@mideohealth.com if it keeps happening.';
  }
}

/**
 * The panel has ONE error slot, so a cleared step's failure must not linger on
 * the next. Anything unscoped (generic, throttling) shows wherever it happens.
 */
function errorBelongsToStep(error: SmsEnrollmentError, step: string): boolean {
  switch (error.kind) {
    case 'bad-password':
    case 'stale-session':
    case 'already-enrolled':
      return step === 'reauth';
    case 'invalid-phone':
    case 'region-not-supported':
    case 'unverified-email':
      return step === 'phone';
    // ⚠️ BOTH send steps (CU-86ajtgauu). Each can be raised by the CODE step's
    // resend button, where a phone-only scope rendered nothing. A failed send
    // does not change the step, so a failed RESEND leaves the patient on
    // "code". The cap was found by the real-pair integration test once its
    // button stopped being disabled; `send-failed` is the same shape. The phone
    // step raises both too (first send, and "Use a different number").
    case 'send-failed':
    case 'resend-cap-reached':
      return step === 'phone' || step === 'code';
    case 'bad-code':
      return step === 'code';
    default:
      return step !== 'done';
  }
}

export interface SmsEnrollmentSectionProps {
  /** Called once the factor is live at Identity Platform. */
  onComplete?: () => void;
  /**
   * ⚠️ Means different things per host. On profile it collapses the section; on
   * the gate it MUST return to the factor choice, never out of the gate — a
   * gated patient must not be able to back out into a wall.
   */
  onCancel?: () => void;
  /**
   * Gate host only: surfaces the passkey-cohort notice and the Tier 0 steer.
   * Off on profile, where the framing is "add a second factor if you like".
   */
  variant?: 'profile' | 'gate';
}

export function SmsEnrollmentSection({ onComplete, onCancel, variant = 'profile' }: SmsEnrollmentSectionProps) {
  const [password, setPassword] = React.useState('');
  /**
   * ⚠️ E.164 as emitted by `PhoneInput`, e.g. `+442079460958`.
   *
   * The patient types their number the way they DIAL it — a UK patient selects
   * their country and types `02079460958`, trunk zero and all — and the
   * component resolves it. They never type a `+`, and they must never have to:
   * requiring one is the CU-86ajrx60c defect wearing a UI costume. A patient
   * who leaves the picker on the wrong country and types a local number gets
   * `null` from the parser and is told their perfectly valid number is
   * malformed.
   */
  const [phone, setPhone] = React.useState<string>('');
  /**
   * The picker's current country, threaded into parsing purely as a FALLBACK.
   * `PhoneInput` normally emits full E.164 (which carries its own country), so
   * this is rarely consulted — it matters for a partially-typed value on blur.
   */
  const [country, setCountry] = React.useState<string | undefined>('US');
  const [code, setCode] = React.useState('');
  /**
   * The number the OTP was actually dispatched to.
   *
   * ⚠️ Not the same as `phone`: "Use a different number" returns to the phone
   * step where `phone` is edited, so a resend must target what was SENT, not
   * what is currently typed.
   */
  const [submittedPhone, setSubmittedPhone] = React.useState('');
  const [verificationEmailSent, setVerificationEmailSent] = React.useState(false);
  const [verificationEmailFailed, setVerificationEmailFailed] = React.useState(false);

  const {
    step,
    busy,
    error,
    regionWarning,
    submitPassword,
    submitPhone,
    submitCode,
    resendVerificationEmail,
    clearError,
    checkRegion,
    changeNumber,
    reset,
    resendCooldownSecs,
    resendCapReached,
  } = useSmsEnrollment(RECAPTCHA_CONTAINER_ID);

  /**
   * ⚠️ FIRE `onComplete` ONCE PER `done` TRANSITION — LATCHED, NOT DEPENDENCY-GUARDED.
   *
   * MEASURED ON STAGING 2026-09-02: this effect drove an unbounded loop that
   * issued **20,379 requests** to `securetoken.googleapis.com` from a single SMS
   * enrolment, rate-limiting the patient out of the very gate they were passing.
   *
   * The cycle, once `step === 'done'`:
   *   1. effect fires → `onComplete()` → `MfaRouteGate`'s `refresh()` → `setNonce`
   *   2. `useMfaRouting` re-renders, so `MfaGateFactorChoice` re-renders
   *   3. the caller's inline arrow is a NEW function identity
   *   4. `onComplete` "changed" → this effect fires again → back to 1
   *
   * `step` stays `'done'` forever, so nothing broke the cycle. Each turn ran
   * `getAuthAssurance(true)` — a FORCED token refresh — which is what made a
   * render loop into a network storm.
   *
   * ⚠️ THE CALLER WAS ALSO FIXED (a `useCallback` in `MfaGateFactorChoice`), and
   * EITHER FIX ALONE WOULD HAVE STOPPED IT. Both are kept deliberately: the
   * caller's stability is a property of one call site today, whereas this latch
   * makes the component correct for EVERY caller, including ones not yet
   * written. A prop identity is not something a component should have to trust.
   *
   * ⚠️ `onComplete` is deliberately NOT in the dependency array. Re-firing on a
   * new function identity is the defect; the latest value is read through a ref
   * so a genuinely changed callback is still honoured on the next transition.
   */
  const onCompleteRef = React.useRef(onComplete);
  onCompleteRef.current = onComplete;
  const completedRef = React.useRef(false);

  React.useEffect(() => {
    if (step !== 'done') {
      // Re-arm, so a subsequent enrolment in the same mount still reports.
      completedRef.current = false;
      return;
    }
    if (completedRef.current) return;
    completedRef.current = true;
    onCompleteRef.current?.();
  }, [step]);

  // A new unverified-email rejection supersedes the previous send's notice.
  React.useEffect(() => {
    if (error?.kind !== 'unverified-email') {
      setVerificationEmailSent(false);
      setVerificationEmailFailed(false);
    }
  }, [error]);

  const visibleError = error && errorBelongsToStep(error, step) ? error : null;

  const handleResendVerification = async () => {
    const ok = await resendVerificationEmail();
    setVerificationEmailSent(ok);
    setVerificationEmailFailed(!ok);
  };

  /**
   * Show a malformed-number hint on BLUR, not per keystroke — otherwise the
   * field screams at the patient while they are still typing it.
   *
   * ⚠️ Three conditions, each one earned on-device (mobile, 2026-08-06). The
   * app originally hardcoded this on the reasoning that `regionWarning` and the
   * submit path carried every message this step needs; testing disproved it.
   *
   *  • `phoneBlurred` — an untouched field is never marked wrong. Marking one
   *    the patient has not reached yet is hostile.
   *  • non-empty — empty is not invalid. The submit button is already disabled
   *    while the field is blank.
   *  • not already showing `invalid-phone` — that submit error renders the SAME
   *    sentence on this step, and two identical messages stacked above and
   *    below the field read as two separate problems.
   */
  const [phoneBlurred, setPhoneBlurred] = React.useState(false);
  const showPhoneInvalid =
    phoneBlurred && phone.trim().length > 0 && formatPhoneE164(phone, country) === null && error?.kind !== 'invalid-phone';

  /** Shared by the phone step's send button and the code step's resend. */
  const resendLabel = resendCapReached
    ? 'No more codes available'
    : resendCooldownSecs > 0
      ? `Send a new code in ${resendCooldownSecs}s`
      : 'Send a new code';

  const sendLabel = resendCapReached
    ? 'No more codes available'
    : resendCooldownSecs > 0
      ? `Send a code in ${resendCooldownSecs}s`
      : 'Send me a code';

  return (
    <div className="my-6 flex flex-col gap-4 rounded-lg border p-4">
      <h3 className="text-lg font-semibold">
        {variant === 'gate' ? 'Add a text-message code to continue' : 'Text-message verification'}
      </h3>

      {variant === 'gate' && (
        <>
          <p className="text-sm">
            To keep your health information secure, your account needs a second way to confirm it&apos;s you. Enter your
            mobile number below and we&apos;ll text you a code each time you sign in.
          </p>
          {/* ⚠️ THE PASSKEY-COHORT NOTICE WAS REMOVED HERE (CU-86ajzr9fp).
              It read "The web portal can't accept passkeys yet — for now it
              needs a text-message code", which became FALSE when sub-task 3
              shipped passkey login (PR #278).

              It was written to be REMOVABLE IN ONE EDIT and deliberately not
              woven into the surrounding sentences, which is exactly what let it
              come out cleanly. That worked; the same discipline is worth
              repeating for the next temporary notice.

              The patient it addressed is now served properly: the gate offers
              "use the passkey you already have" as its FIRST option, rather
              than telling them their passkey doesn't count here. */}
        </>
      )}

      {variant === 'profile' && step !== 'done' && (
        <p className="text-sm">
          Add a text-message code to your sign-in. We&apos;ll text you a code each time you sign in, so your health
          information stays protected even if someone learns your password.
        </p>
      )}

      {step === 'reauth' && (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!busy && password) void submitPassword(password);
          }}
        >
          <p className="text-sm">For your security, please confirm your password before adding a sign-in method.</p>
          <PasswordInput
            id="mfa-enroll-password"
            title="Confirm your password"
            password={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button
            id="mfa-enroll-password-submit"
            type="submit"
            onClick={(e) => {
              e.preventDefault();
              if (!busy && password) void submitPassword(password);
            }}
            textContent="Confirm and continue"
            disabled={busy || !password}
            working={busy}
            workingText="Checking..."
          />
        </form>
      )}

      {step === 'phone' && (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!busy && phone) {
              setSubmittedPhone(phone);
              void submitPhone(phone, country);
            }
          }}
        >
          <label htmlFor="mfa-enroll-phone" className="text-sm font-medium">
            Your mobile number
          </label>
          {/* ⚠️ NOT `international`. With that flag the field displays and
              demands full E.164, so a patient must type "+44…" themselves —
              which is exactly the burden CU-86ajrx60c removed at the parsing
              layer and must not be reintroduced here. Without it, the patient
              picks their country and types the number the way they dial it
              (UK: `02079460958`, trunk zero included) and this emits
              `+442079460958`.

              `countrySelectProps` reports the picker's country so a
              partially-typed value can still be region-checked on blur. */}
          <PhoneInput
            id="mfa-enroll-phone"
            defaultCountry="US"
            // ⚠️ Pinned to the top WITHOUT hiding the rest — deliberately not
            // `countries`, which would REMOVE every other option. The server
            // allowlist is hand-edited and this mirror drifts; hiding countries
            // would turn that drift into "your country does not exist".
            countryOptionsOrder={['US', 'CA', 'AU', '|', '...']}
            placeholder="Enter your mobile number"
            value={phone}
            onChange={(value) => {
              setPhone(value ?? '');
              // Editing invalidates the previous verdict. Without this the hint
              // stays up while the patient corrects the number and reads as
              // frozen — the same failure the sticky-error fix addressed for
              // submit errors (measured on-device 2026-07-29).
              setPhoneBlurred(false);
            }}
            onCountryChange={(next) => setCountry(next ?? undefined)}
            // Layer 1 fires on BLUR, not per keystroke — otherwise every
            // half-typed number flashes a warning. It also clears a stale error
            // so a rejection does not read as frozen while the patient corrects
            // the number (measured on-device 2026-07-29).
            onBlur={() => {
              setPhoneBlurred(true);
              checkRegion(phone, country);
            }}
            className="rounded border px-3 py-2"
          />
          {showPhoneInvalid && (
            // A MALFORMED number, distinct from an unsupported region. Same
            // sentence as the `invalid-phone` submit error on purpose — the
            // patient should not have to reconcile two wordings for one
            // problem — which is also why that error suppresses this hint.
            <p className="text-sm text-red-700">
              That doesn&apos;t look like a complete mobile number. Please check it and try again.
            </p>
          )}
          {regionWarning && !visibleError && !showPhoneInvalid && (
            // Advisory only. The server is the authority, and it may accept a
            // number this mirror doubts — so this warns without blocking.
            <p className="text-sm text-amber-700">
              We may not be able to send text messages to that number&apos;s country. You can still try, or contact
              info@mideohealth.com.
            </p>
          )}
          <Button
            id="mfa-enroll-phone-submit"
            type="submit"
            onClick={(e) => {
              e.preventDefault();
              if (!busy && phone) {
                setSubmittedPhone(phone);
                void submitPhone(phone, country);
              }
            }}
            textContent={sendLabel}
            // ⚠️ NOT disabled at the cap (CU-86ajtgauu) — the hook's cap
            // branch (Sentry signal + "start over" copy) is reached only by a
            // press. Not gated on the cooldown once capped, either.
            disabled={busy || !phone || (!resendCapReached && resendCooldownSecs > 0)}
            working={busy}
            workingText="Sending..."
          />
        </form>
      )}

      {step === 'code' && (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!busy && code.trim()) void submitCode(code.trim());
          }}
        >
          <label htmlFor="mfa-enroll-code" className="text-sm font-medium">
            Enter the code we texted you
          </label>
          <input
            id="mfa-enroll-code"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              // ⚠️ Without this the rejection reads as FROZEN while a corrected
              // code is typed, and the patient concludes the new code failed
              // too. Measured on mobile 2026-07-29 and again on the portal
              // 2026-08-07 (§A3) — the challenge form had this from the start;
              // the enrollment form did not.
              if (visibleError) clearError();
            }}
            placeholder="6-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            aria-invalid={!!visibleError}
            aria-describedby={visibleError ? 'mfa-enroll-error' : undefined}
            className="rounded border px-3 py-2"
            disabled={busy}
          />
          <Button
            id="mfa-enroll-code-submit"
            type="submit"
            onClick={(e) => {
              e.preventDefault();
              if (!busy && code.trim()) void submitCode(code.trim());
            }}
            textContent="Confirm code"
            disabled={busy || !code.trim()}
            working={busy}
            workingText="Confirming..."
          />

          {/* ⚠️ WITHOUT THESE, A PATIENT WHOSE SMS NEVER ARRIVES IS STUCK.
              "Start over" was the only exit, and it discards a valid
              authorization and a cleared password step — then burns another
              mint against the 5-per-300s limiter to get back here.

              The console has the same gap; mobile does not. Found on staging
              2026-08-07 while testing a wrong code (CU-86ajwpnek §A3). */}
          <div className="flex flex-col gap-1 text-sm">
            <button
              type="button"
              onClick={() => {
                // Re-send to the SAME number. Clearing the typed code matters:
                // it belongs to the superseded OTP, and leaving it would invite
                // submitting a code that can no longer succeed. Except AT the
                // cap: no new code is coming, so keep the one they typed.
                if (!resendCapReached) setCode('');
                void submitPhone(submittedPhone, country);
              }}
              // ⚠️ NOT disabled at the cap (CU-86ajtgauu) — see the send button.
              disabled={busy || (!resendCapReached && resendCooldownSecs > 0)}
              className="self-start underline disabled:no-underline disabled:opacity-60"
            >
              {resendLabel}
            </button>
            {/* ⚠️ Resend alone is not enough. A patient who mistyped ONE digit
                has sent a code to a stranger, and resending sends them another.
                They need to correct the number without losing the ceremony. */}
            <button
              type="button"
              onClick={() => {
                setCode('');
                changeNumber();
              }}
              disabled={busy}
              className="self-start underline"
            >
              Use a different number
            </button>
          </div>
        </form>
      )}

      {step === 'done' && (
        <div className="flex flex-col gap-2">
          <p className="font-semibold">Text-message verification is on for your account.</p>
          <p className="text-sm">
            From now on you&apos;ll be asked for a texted code each time you sign in. You can carry on right away — no
            need to sign in again.
          </p>
          {/* ✅ MEASURED 2026-08-07 (§E2). Enrollment DOES revoke existing
              refresh tokens — but NOT immediately, and the delay is the whole
              point of this wording.

              An app session enrolled from the portal kept working for ~47
              minutes, then signed itself out (MIDEO-UNIFIED-90 / -8Z, same
              trace, 11ms apart: the Firestore listener saw
              `firestore/permission-denied` with `hasAuthUser: false`, then the
              auth layer reported "session revoked mid-session"). The access
              token stayed valid until it expired; only then did the refresh
              exchange fail.

              ⚠️ So "you'll need to sign in again" is TRUE but reads as
              immediate, and "you may need to" understates a certainty. The
              honest version names the delay — otherwise a patient sees the
              other device still working, concludes the warning was wrong, and
              is then surprised an hour later. */}
          <p className="text-sm">
            If you&apos;re signed in on another device, you&apos;ll be asked to sign in there again within the hour —
            that&apos;s expected.
          </p>
        </div>
      )}

      {visibleError && (
        <div id="mfa-enroll-error" role="alert" className="flex flex-col gap-2 text-sm text-red-700">
          <p>{errorCopy(visibleError)}</p>

          {/* ⚠️ THE ONLY WAY A PATIENT CAN UNBLOCK THEMSELVES. The Firebase
              console has NO admin-side "send verification email" action, so
              without this a patient whose original email never arrived has no
              route forward at all — not even through support. On the GATE, post
              cutover, that is a lockout from their own PHI. */}
          {visibleError.kind === 'unverified-email' && (
            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={() => void handleResendVerification()}
                className="self-start underline"
                disabled={busy}
              >
                Send me the verification email again
              </button>
              {verificationEmailSent && (
                <span className="text-green-700">Sent. Check your inbox, then come back and try again.</span>
              )}
              {verificationEmailFailed && (
                <span>
                  We couldn&apos;t send it just now. Please try again shortly, or contact info@mideohealth.com.
                </span>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-4 text-sm">
        {step !== 'done' && step !== 'reauth' && (
          <button type="button" onClick={reset} className="underline" disabled={busy}>
            Start over
          </button>
        )}
        {onCancel && step !== 'done' && (
          <button type="button" onClick={onCancel} className="underline" disabled={busy}>
            {/* ⚠️ On the gate this returns to the factor choice, NOT out of the
                gate. The host owns that; this only reports the intent. */}
            {variant === 'gate' ? 'Choose a different method' : 'Not now'}
          </button>
        )}
      </div>

      {/* ⚠️ THIS IS A STABLE WRAPPER, NOT THE reCAPTCHA CONTAINER ITSELF.
          React must never own the container: `grecaptcha` marks whichever
          ELEMENT it rendered into as occupied, and that marker lives inside
          Google's script rather than in DOM we can clear — so a resend needs a
          genuinely NEW element, which React reconciliation would fight us over.
          The hook creates and removes the real container inside this wrapper as
          a plain DOM child React knows nothing about (see `prepareContainer`).

          Must never be conditionally rendered — the hook looks it up by id. */}
      <div id={`${RECAPTCHA_CONTAINER_ID}-host`} />
    </div>
  );
}

export default SmsEnrollmentSection;
